import { diveService } from '../services/api';
import { Dive } from '../types/Dive';

export interface DemoData {
  dives: Dive[];
  exportDate: string;
  version: string;
}

export class DemoDataExporter {
  private static readonly STORAGE_KEY = 'aidivecoach_demo_data';
  private static readonly VERSION = '1.0.0';

  /**
   * Export current user's data to create demo dataset
   * This should be run manually with a valid access token
   */
  static async exportCurrentData(accessToken: string): Promise<DemoData> {
    console.log('🎬 Starting demo data export...');

    try {
      // Fetch all current data
      const dives = await diveService.getDives(accessToken, 100); // Limit to reasonable amount

      const demoData: DemoData = {
        dives,
        exportDate: new Date().toISOString(),
        version: this.VERSION
      };

      console.log('📊 Demo data exported:', {
        divesCount: dives.length,
        exportDate: demoData.exportDate
      });

      return demoData;
    } catch (error) {
      console.error('❌ Error exporting demo data:', error);
      throw error;
    }
  }

  /**
   * Save demo data to localStorage
   */
  static saveDemoData(demoData: DemoData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(demoData));
      console.log('💾 Demo data saved to localStorage');
    } catch (error) {
      console.error('❌ Error saving demo data:', error);
      throw error;
    }
  }

  /**
   * Load demo data from localStorage
   */
  static loadDemoData(): DemoData | null {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) return null;

      const demoData = JSON.parse(data) as DemoData;
      console.log('📂 Demo data loaded from localStorage:', {
        divesCount: demoData.dives?.length || 0,
        exportDate: demoData.exportDate
      });

      return demoData;
    } catch (error) {
      console.error('❌ Error loading demo data:', error);
      return null;
    }
  }

  /**
   * Check if demo data exists
   */
  static hasDemoData(): boolean {
    return localStorage.getItem(this.STORAGE_KEY) !== null;
  }

  /**
   * Clear demo data
   */
  static clearDemoData(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    console.log('🗑️ Demo data cleared');
  }

  /**
   * Export and save current data (convenience method)
   */
  static async exportAndSave(accessToken: string): Promise<void> {
    const demoData = await this.exportCurrentData(accessToken);
    this.saveDemoData(demoData);
  }
}

// Global function to manually export data from browser console
(window as any).exportDemoData = async () => {
  const accessToken = localStorage.getItem('suunto_access_token');
  if (!accessToken) {
    console.error('❌ No access token found. Please authenticate with Suunto first.');
    return;
  }

  try {
    await DemoDataExporter.exportAndSave(accessToken);
    console.log('✅ Demo data export complete! Users can now use demo mode.');
  } catch (error) {
    console.error('❌ Demo data export failed:', error);
  }
};

console.log('🎬 Demo data exporter loaded. Run exportDemoData() in console to create demo dataset.');